import type { ISamplePool } from 'Model/index.js'

export const hasCorrectFileEnding = (fileName: string) => fileName.includes('.gz')

export const checkIsFileEndingCorrect = (files: File[]) => {
  return files.every((file) => hasCorrectFileEnding(file.name))
}

export const hasReadDesignation = (fileName: string) => {
  const name = fileName.toLowerCase()
  return (
    name.includes('_r1') ||
    name.includes('_r2') ||
    name.endsWith('.ft.fastq.gz') ||
    name.endsWith('.rt.fastq.gz') ||
    name.includes('.r1') ||
    name.includes('.r2') ||
    name.endsWith('_ft.fastq.gz') ||
    name.endsWith('_rt.fastq.gz')
  )
}

export const checkFileHasPairingInfo = (files: File[]) => {
  return files.every((file) => hasReadDesignation(file.name))
}

/**
 * The names in `fileNames` that have no R1/R2 partner — looking both inside the selection itself and
 * in `alreadyPresent` (files already uploaded to the target pool, or already queued in the uploader).
 *
 * Looking outside the selection is the whole point: a retry after a partly-failed upload legitimately
 * contains only one half of a pair, because the other half is already in the pool. Judging the
 * selection on its own is what produced the bogus "please upload files in pairs" on re-upload.
 */
export const findUnpairedFileNames = (fileNames: string[], alreadyPresent: readonly string[] = []): string[] => {
  const available = new Set<string>([...fileNames, ...alreadyPresent])
  return fileNames.filter((name) => {
    const pairName = getPairName(name)
    return !pairName || !available.has(pairName)
  })
}

export const getSequenceIdFromFilename = (filename: string) => {
  return filename.replaceAll('-', '_')
}

/** A sheet's sequence id names a file when it is a prefix of the file name, `-` read as `_`. */
export const sequenceIdFits = (sequenceId: string, fileName: string): boolean => {
  const id = getSequenceIdFromFilename(sequenceId.trim())
  return id !== '' && getSequenceIdFromFilename(fileName).startsWith(id)
}

export const checkFileNamesAreUnique = (datasets: Partial<ISamplePool>[], files: File[]): boolean => {
  const addedFileNames = new Set<string>()

  for (const dataset of datasets) {
    for (const sample of dataset.samples || []) {
      if (sample.forwardFile) addedFileNames.add(sample.forwardFile.fileName)
      if (sample.reverseFile) addedFileNames.add(sample.reverseFile.fileName)
    }

    for (const negativeControl of dataset.negativeControls || []) {
      if (negativeControl.forwardFile) addedFileNames.add(negativeControl.forwardFile.fileName)
      if (negativeControl.reverseFile) addedFileNames.add(negativeControl.reverseFile.fileName)
    }
  }

  return !files.some((file) => addedFileNames.has(file.name))
}

export const getPairName = (fileName: string): string | null => {
  if (fileName.includes('_R1')) return fileName.replace('_R1', '_R2')
  if (fileName.includes('_R2')) return fileName.replace('_R2', '_R1')
  if (fileName.endsWith('.ft.fastq.gz')) return fileName.replace('.ft.fastq.gz', '.rt.fastq.gz')
  if (fileName.endsWith('.rt.fastq.gz')) return fileName.replace('.rt.fastq.gz', '.ft.fastq.gz')
  if (fileName.includes('.r1')) return fileName.replace('.r1', '.r2')
  if (fileName.includes('.r2')) return fileName.replace('.r2', '.r1')
  if (fileName.endsWith('_ft.fastq.gz')) return fileName.replace('_ft.fastq.gz', '_rt.fastq.gz')
  if (fileName.endsWith('_rt.fastq.gz')) return fileName.replace('_rt.fastq.gz', '_ft.fastq.gz')
  return null
}

export const sampleIsForward = (file: string) => {
  return ['_R1', '.r1', '_ft.fastq.gz', '.ft.fastq.gz'].some((tag) =>
    file.toLowerCase().includes(tag)
  )
}